'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { openEventStream } from '@/lib/api/event-stream'
import { useWorkspaceStore } from '@/stores'
import { ACTIVITIES_STREAM_PATH, activitiesKeys, fetchActivities, parseSnapshot } from './api'
import { ACTIVITIES_CHANGED_EVENT } from './events'
import { isProgressing, type ActivityDto } from './types'

/** While something runs the snapshot is read every two seconds (the same cadence as the S&R page). */
export const ACTIVITIES_POLL_ACTIVE_MS = 2_000
/** With nothing running, every ten: to notice work somebody else started. */
export const ACTIVITIES_POLL_IDLE_MS = 10_000
/**
 * After the dock is told something started (a mutation, `notifyActivitiesChanged`) it reads every
 * two seconds for this long whatever the stream says: the server shares one answer for a moment,
 * so the very first read can still be the one from before the work began.
 */
export const ACTIVITIES_BURST_MS = 12_000

/** live: the stream is open. polling: reading on a timer. offline: the last read failed (what is shown is stale). */
export type ActivitiesConnection = 'live' | 'polling' | 'offline'

export interface UseActivitiesOptions {
  /** Signed in, a workspace chosen, a page that shows chrome, a role that can see any activity. */
  enabled: boolean
  /**
   * Query-key prefixes whose invalidation means work may have changed (the Email Remediator's
   * mutations invalidate its search-remediate keys when a run or an action starts).
   */
  refreshKeys?: readonly (readonly unknown[])[]
}

/**
 * How long to wait before reading again, or false for "do not poll". Fast while work runs or just
 * started (the burst), never while the stream is live (it pushes the whole snapshot), slow otherwise.
 */
export function activitiesPollInterval(opts: { burstActive: boolean; streamLive: boolean; progressing: boolean | undefined }): number | false {
  if (opts.burstActive) return ACTIVITIES_POLL_ACTIVE_MS
  if (opts.streamLive) return false
  return opts.progressing ? ACTIVITIES_POLL_ACTIVE_MS : ACTIVITIES_POLL_IDLE_MS
}

const sameKey = (key: readonly unknown[], prefix: readonly unknown[]): boolean =>
  prefix.length <= key.length && prefix.every((part, i) => JSON.stringify(part) === JSON.stringify(key[i]))

/**
 * The dock's data: one snapshot of every activity the viewer can see, live over server-sent events
 * (`GET /v1/activities/stream`, authenticated by header, never a token in a URL) with a poll as the
 * fallback. A stream frame is the WHOLE snapshot, not a delta, so it replaces the cached one: there
 * is no list for it to disagree with. The stream is bound to the workspace it was opened in, so a
 * workspace switch closes it and opens another; a stream the server refuses (429: too many tabs)
 * is not retried and the poll carries on alone.
 */
export function useActivities({ enabled, refreshKeys = [] }: UseActivitiesOptions) {
  const qc = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const [streamLive, setStreamLive] = useState(false)
  const burstUntil = useRef(0)

  const query = useQuery({
    queryKey: activitiesKeys.snapshot(),
    queryFn: fetchActivities,
    enabled,
    refetchInterval: (q) =>
      activitiesPollInterval({
        burstActive: Date.now() < burstUntil.current,
        streamLive,
        progressing: q.state.data?.activities.some((a: ActivityDto) => isProgressing(a.state)),
      }),
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 60_000,
    retry: 1,
  })

  const { refetch } = query
  const refresh = useCallback(() => {
    burstUntil.current = Date.now() + ACTIVITIES_BURST_MS
    void refetch({ cancelRefetch: false })
  }, [refetch])

  // A module told us something started.
  useEffect(() => {
    if (!enabled) return
    window.addEventListener(ACTIVITIES_CHANGED_EVENT, refresh)
    return () => window.removeEventListener(ACTIVITIES_CHANGED_EVENT, refresh)
  }, [enabled, refresh])

  // A module's own queries were invalidated: it started or finished something.
  const watched = JSON.stringify(refreshKeys)
  useEffect(() => {
    if (!enabled || refreshKeys.length === 0) return
    return qc.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'invalidate' && refreshKeys.some((k) => sameKey(event.query.queryKey, k))) {
        refresh()
      }
    })
    // `watched` is the keys' value; the array itself is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, qc, refresh, watched])

  // The live stream.
  useEffect(() => {
    if (!enabled || !workspaceId || workspaceId === 'all') return
    const close = openEventStream(ACTIVITIES_STREAM_PATH, {
      onEvent: (name, data) => {
        if (name !== 'snapshot') return
        const snapshot = parseSnapshot(data)
        if (snapshot) qc.setQueryData(activitiesKeys.snapshot(), snapshot)
      },
      onStatus: (status) => setStreamLive(status === 'live'),
      onRefused: () => 'stop',
      // The server pings every 25 s; three missed pings is a half-open connection.
      heartbeatTimeoutMs: 75_000,
    })
    return () => {
      close()
      setStreamLive(false)
    }
  }, [enabled, workspaceId, qc])

  const connection: ActivitiesConnection = streamLive ? 'live' : query.isError ? 'offline' : 'polling'
  return {
    activities: query.data?.activities,
    unavailable: query.data?.unavailable ?? [],
    /** True once one snapshot has arrived (the first read is the baseline for what is announced). */
    loaded: query.data !== undefined,
    connection,
    refresh,
  }
}
